/**
 * Local calendar days.
 *
 * Everything here avoids `toISOString()`, which is UTC. West of Greenwich it
 * returns tomorrow's date all evening, so "is this today?" fails its own check
 * after 7pm and every workout logged in the evening looks backdated. The
 * calendar, the scheduler and the log screen all compare these strings, so they
 * all have to agree on what a day is.
 */

import { parseIso } from './time'

/** Today, YYYY-MM-DD, local time. */
export function todayIso(): string {
  return dayIso(new Date())
}

export function dayIso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-` +
    `${String(d.getDate()).padStart(2, '0')}`
}

/**
 * The local day a stored timestamp belongs to.
 *
 * Never `iso.slice(0, 10)`. That takes the day off the *UTC* clock, and every
 * comparison in this app is against a local day — so from 8pm onward a workout
 * started an hour ago failed `=== todayIso()` and the screen decided it was
 * looking at some other day's session. It read as a mid-workout glitch, and it
 * was one character of laziness on a string that happens to start with a date.
 */
export function dayOf(iso: string): string {
  return dayIso(parseIso(iso))
}

/**
 * The `started_at` for a workout on `day`, or undefined for today.
 *
 * Keeps the current clock time and moves only the date, so a session recorded
 * three days late sits at a plausible hour instead of midnight — which reads
 * as a real workout rather than an import artefact, and keeps "morning or
 * evening" sortable within the day.
 *
 * Returns a real UTC instant. It used to return the local wall clock with no
 * offset at all — which put two different conventions inside one row, since
 * every other timestamp comes from `toISOString()`. A workout backdated at 5:10
 * PM was stored as 17:10 UTC, i.e. 1:10 PM, so it finished before it started,
 * its duration was garbage, and the server bucketed it onto the wrong calendar
 * square while `local_date` faithfully converted a timestamp that was never UTC.
 */
export function startedAtFor(day: string): string | undefined {
  if (day === todayIso()) return undefined
  const now = new Date()
  const [y, m, d] = day.split('-').map(Number)
  // Built through the local Date constructor, so the offset that applied on
  // *that* date is the one used — not today's, which differs across a DST edge.
  return new Date(y, m - 1, d, now.getHours(), now.getMinutes(), 0, 0).toISOString()
}
